import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

function getTypeScaleStyle(config) {
  const typeScale = config.typeScale || {};

  return {
    '--tpl-user-title-scale': typeScale.title || 1,
    '--tpl-user-heading-scale': typeScale.heading || 1,
    '--tpl-user-body-scale': typeScale.body || 1,
    '--tpl-user-price-scale': typeScale.price || 1,
    '--tpl-user-description-scale': typeScale.description || 1,
    '--tpl-description-weight': config.descriptionBold === false ? 400 : 700
  };
}

function computeDensityClass(categories, config) {
  const allItems = [];
  categories.forEach(cat => {
    cat.items.forEach(item => {
      allItems.push(item);
    });
  });

  const categoryCount = categories.length;
  const itemCount = allItems.length;
  const longestItemName = allItems.reduce((max, item) => {
    const length = (item.name || '').length;
    return length > max ? length : max;
  }, 0);

  const isFrontOnly = config.pages === 'front-only';

  const densityScore =
    itemCount +
    Math.max(0, categoryCount - 3) * 1.5 +
    Math.max(0, longestItemName - 24) / 8 +
    (isFrontOnly ? 4 : 0); // Extra density boost for 1-page front-only constraint

  if (densityScore > 16 || (isFrontOnly && itemCount > 12)) return 'tpl-density-compact';
  if (densityScore > 10 || config.layout === 'half' || (isFrontOnly && itemCount > 7)) return 'tpl-density-tight';
  return 'tpl-density-comfy';
}

function paginateByMeasurements(categories, measurements) {
  if (!measurements || !measurements.availableHeight) {
    return categories.length > 0 ? [categories] : [[]];
  }

  const {
    availableHeight,
    sectionGap,
    categoryGap,
    itemGap,
    headerHeights,
    itemHeights
  } = measurements;

  const pages = [];
  let currentPage = [];
  let usedHeight = 0;

  const pushPage = () => {
    if (currentPage.length === 0) return;
    pages.push(currentPage);
    currentPage = [];
    usedHeight = 0;
  };

  categories.forEach((category, catIdx) => {
    let pageCategory = null;
    const headerHeight = headerHeights[catIdx] || 0;

    category.items.forEach((item, itemIdx) => {
      const itemHeight = itemHeights[`${catIdx}-${itemIdx}`] || 0;
      const isNewCategoryOnPage = !pageCategory;

      let additionalHeight = itemHeight;
      if (isNewCategoryOnPage) {
        additionalHeight += headerHeight;
        if (category.items.length > 0) additionalHeight += categoryGap;
        if (currentPage.length > 0) additionalHeight += sectionGap;
      } else if (pageCategory.items.length > 0) {
        additionalHeight += itemGap;
      }

      if (currentPage.length > 0 && usedHeight + additionalHeight > availableHeight) {
        pushPage();
        pageCategory = null;
        additionalHeight = itemHeight + headerHeight + (category.items.length > 0 ? categoryGap : 0);
      }

      if (!pageCategory) {
        pageCategory = { name: category.name, items: [] };
        currentPage.push(pageCategory);
        usedHeight += headerHeight;
        if (category.items.length > 0) usedHeight += categoryGap;
      } else {
        usedHeight += itemGap;
      }

      pageCategory.items.push(item);
      usedHeight += itemHeight;
    });

    if (category.items.length === 0) {
      const additionalHeight = headerHeight + (currentPage.length > 0 ? sectionGap : 0);
      if (currentPage.length > 0 && usedHeight + additionalHeight > availableHeight) {
        pushPage();
      }
      currentPage.push({ ...category, items: [] });
      usedHeight += headerHeight;
    }
  });

  pushPage();
  return pages.length > 0 ? pages : [[]];
}

function useMeasuredPagination(categories, venue, config, pageLabel, showCategoryHeaders) {
  const densityClass = useMemo(
    () => computeDensityClass(categories, config),
    [categories, config]
  );
  const templateClass = `menu-tpl-${config.template}`;
  const layoutClass = config.layout === 'half' ? 'tpl-half' : 'tpl-full';
  const typeScaleStyle = getTypeScaleStyle(config);
  const itemsContainerRef = useRef(null);
  const categoryHeaderRefs = useRef({});
  const itemRefs = useRef({});
  const categoryRef = useRef(null);
  const categoryItemsRef = useRef(null);
  const [pages, setPages] = useState(categories.length > 0 ? [categories] : [[]]);

  const measurementKey = JSON.stringify({
    categories,
    template: config.template,
    layout: config.layout,
    pageLabel,
    venueName: venue?.name || '',
    densityClass,
    showCategoryHeaders,
    typeScale: config.typeScale || null,
    descriptionBold: config.descriptionBold !== false
  });

  useLayoutEffect(() => {
    const itemsContainer = itemsContainerRef.current;
    if (!itemsContainer) return;

    const availableHeight = itemsContainer.clientHeight;
    const sectionGap = parseFloat(window.getComputedStyle(itemsContainer).rowGap || window.getComputedStyle(itemsContainer).gap || '0') || 0;
    const categoryGap = categoryRef.current
      ? parseFloat(window.getComputedStyle(categoryRef.current).rowGap || window.getComputedStyle(categoryRef.current).gap || '0') || 0
      : 0;
    const itemGap = categoryItemsRef.current
      ? parseFloat(window.getComputedStyle(categoryItemsRef.current).rowGap || window.getComputedStyle(categoryItemsRef.current).gap || '0') || 0
      : 0;

    const headerHeights = {};
    const itemHeights = {};

    Object.entries(categoryHeaderRefs.current).forEach(([key, element]) => {
      if (element) headerHeights[key] = element.offsetHeight;
    });

    Object.entries(itemRefs.current).forEach(([key, element]) => {
      if (element) itemHeights[key] = element.offsetHeight;
    });

    const nextPages = paginateByMeasurements(categories, {
      availableHeight,
      sectionGap,
      categoryGap,
      itemGap,
      headerHeights,
      itemHeights
    });

    setPages(prevPages => {
      const prevSerialized = JSON.stringify(prevPages);
      const nextSerialized = JSON.stringify(nextPages);
      return prevSerialized === nextSerialized ? prevPages : nextPages;
    });
  }, [measurementKey, categories]);

  const measureLayer = (
    <div className="tpl-measure-layer" aria-hidden="true">
      <div
        className={`tpl-page ${templateClass} ${layoutClass} ${densityClass} tpl-measure-page`}
        style={typeScaleStyle}
      >
        <div className={config.layout === 'half' ? 'tpl-half-col' : undefined}>
          <div className="tpl-content">
            <div className="tpl-inner">
              <header className="tpl-header">
                {venue?.name ? (
                  <p className="tpl-eyebrow">{venue.name}</p>
                ) : null}
                <h1 className="tpl-title">{pageLabel || config.mealType}</h1>
                <div className="tpl-title-ornament"></div>
              </header>

              <div ref={itemsContainerRef} className="tpl-items">
                {showCategoryHeaders ? (
                  categories.map((category, catIdx) => (
                    <section
                      key={`measure-${catIdx}`}
                      ref={catIdx === 0 ? categoryRef : null}
                      className="tpl-category"
                    >
                      <h2
                        ref={element => { categoryHeaderRefs.current[catIdx] = element; }}
                        className="tpl-cat-name"
                      >
                        {category.name}
                      </h2>
                      <div
                        ref={catIdx === 0 ? categoryItemsRef : null}
                        className="tpl-category-items"
                      >
                        {category.items.map((item, itemIdx) => (
                          <article
                            key={`measure-item-${catIdx}-${itemIdx}`}
                            ref={element => { itemRefs.current[`${catIdx}-${itemIdx}`] = element; }}
                            className="tpl-item"
                          >
                            <div className="tpl-item-line">
                              <h3 className="tpl-item-name">{item.name}</h3>
                              <span className="tpl-item-rule" aria-hidden="true"></span>
                              <p className="tpl-item-price">{item.price}</p>
                            </div>
                            {item.description ? (
                              <p className="tpl-item-desc">{item.description}</p>
                            ) : null}
                          </article>
                        ))}
                      </div>
                    </section>
                  ))
                ) : (
                  categories.flatMap((category, catIdx) =>
                    category.items.map((item, itemIdx) => (
                      <article
                        key={`measure-item-${catIdx}-${itemIdx}`}
                        ref={element => { itemRefs.current[`${catIdx}-${itemIdx}`] = element; }}
                        className="tpl-item"
                      >
                        <div className="tpl-item-line">
                          <h3 className="tpl-item-name">{item.name}</h3>
                          <span className="tpl-item-rule" aria-hidden="true"></span>
                          <p className="tpl-item-price">{item.price}</p>
                        </div>
                        {item.description ? (
                          <p className="tpl-item-desc">{item.description}</p>
                        ) : null}
                      </article>
                    ))
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return { pages, densityClass, measureLayer };
}

function renderItem(item, index) {
  return (
    <article key={`${item.name || 'item'}-${index}`} className="tpl-item">
      <div className="tpl-item-line">
        <h3 className="tpl-item-name">{item.name}</h3>
        <span className="tpl-item-rule" aria-hidden="true"></span>
        <p className="tpl-item-price">{item.price}</p>
      </div>
      {item.description && (
        <p className="tpl-item-desc">{item.description}</p>
      )}
    </article>
  );
}

function shouldCenterPageContent(categories) {
  const itemCount = categories.reduce((total, category) => total + category.items.length, 0);
  return itemCount > 0 && itemCount <= 10;
}

function MenuPage({ categories, venue, config, pageLabel, densityClass, showCategoryHeaders }) {
  const templateClass = `menu-tpl-${config.template}`;
  const layoutClass = config.layout === 'half' ? 'tpl-half' : 'tpl-full';
  const typeScaleStyle = getTypeScaleStyle(config);
  const shouldCenterContent = shouldCenterPageContent(categories);

  const allItems = [];
  categories.forEach(cat => {
    cat.items.forEach(item => {
      allItems.push(item);
    });
  });

  const renderMenuContent = () => (
    <div className="tpl-content">
      {/* Watermark logo */}
      {venue?.logo_base64 && (
        <div className="tpl-watermark">
          <img src={venue.logo_base64} alt="" />
        </div>
      )}

      <div className={`tpl-inner ${shouldCenterContent ? 'tpl-inner-balanced' : ''}`}>
        <header className="tpl-header">
          {venue?.name ? (
            <p className="tpl-eyebrow">{venue.name}</p>
          ) : null}
          <h1 className="tpl-title">{pageLabel || config.mealType}</h1>
          <div className="tpl-title-ornament"></div>
        </header>

        <div className="tpl-items">
          {showCategoryHeaders ? (
            categories.map((cat, ci) => (
              <section key={ci} className="tpl-category">
                <h2 className="tpl-cat-name">{cat.name}</h2>
                <div className="tpl-category-items">
                  {cat.items.map((item, ii) => renderItem(item, ii))}
                </div>
              </section>
            ))
          ) : (
            allItems.map((item, i) => renderItem(item, i))
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`tpl-page ${templateClass} ${layoutClass} ${densityClass}`}
      style={typeScaleStyle}
    >
      {config.layout === 'half' ? (
        <>
          <div className="tpl-half-col">{renderMenuContent()}</div>
          <div className="tpl-cut-line"></div>
          <div className="tpl-half-col">{renderMenuContent()}</div>
        </>
      ) : (
        renderMenuContent()
      )}
    </div>
  );
}

export default function MenuPreview({ menu, venue, config, zoom = 1, containerRef = null }) {
  const scrollerRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(860);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const updateWidth = () => setCanvasWidth(scroller.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  const canvasScale = Math.min(1, canvasWidth / 860) * zoom;
  const backCats = config.backCategories || [];

  let frontCategories, backCategories;

  if (config.pages === 'front-back' && backCats.length > 0) {
    frontCategories = menu.categories.filter(c => !backCats.includes(c.name));
    backCategories = menu.categories.filter(c => backCats.includes(c.name));
  } else {
    frontCategories = menu.categories;
    backCategories = [];
  }

  const showFrontCategoryHeaders = frontCategories.length > 1;
  const showBackCategoryHeaders = backCategories.length > 1;

  const {
    pages: frontPages,
    densityClass: frontDensityClass,
    measureLayer: frontMeasureLayer
  } = useMeasuredPagination(
    frontCategories,
    venue,
    config,
    config.mealType,
    showFrontCategoryHeaders
  );
  const {
    pages: backPages,
    densityClass: backDensityClass,
    measureLayer: backMeasureLayer
  } = useMeasuredPagination(
    backCategories,
    venue,
    config,
    'Desserts',
    showBackCategoryHeaders
  );

  const renderPages = (pages, sideLabel, pageLabel, densityClass, showCategoryHeaders) => (
    pages.map((pageCategories, index) => {
      const label =
        config.pages === 'front-back'
          ? (pages.length === 1 ? sideLabel : `${sideLabel} ${index + 1}`)
          : (pages.length > 1 ? `Page ${index + 1}` : '');

      return (
        <React.Fragment key={`${sideLabel}-${index}`}>
          {label ? (
            <div className={`preview-label ${sideLabel === 'Back' || (config.pages !== 'front-back' && index > 0) ? 'back-label' : ''}`}>
              {label}
            </div>
          ) : null}
          <MenuPage
            categories={pageCategories}
            venue={venue}
            config={config}
            pageLabel={pageLabel}
            densityClass={densityClass}
            showCategoryHeaders={showCategoryHeaders}
          />
        </React.Fragment>
      );
    })
  );

  return (
    <div className="preview-zoom-scroller" ref={scrollerRef}>
      <div
        className="preview-zoom-frame"
        style={{
          zoom: canvasScale,
          width: 860
        }}
      >
        <div className="preview-container" ref={containerRef}>
          {renderPages(frontPages, 'Front', config.mealType, frontDensityClass, showFrontCategoryHeaders)}
          {config.pages === 'front-back' && backPages.length > 0 && renderPages(backPages, 'Back', 'Desserts', backDensityClass, showBackCategoryHeaders)}
        </div>
      </div>
      {frontMeasureLayer}
      {config.pages === 'front-back' && backCategories.length > 0 ? backMeasureLayer : null}
    </div>
  );
}
